/* eslint-disable no-debugger */
import { use, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import NavLinks from '../NavLinks';

import ClubConstants from '../../club/utils/ClubConstants';

import useClubRegistrationNavData from '../../../state/clubRegistration/leftNavigation/UseClubRegistrationNavData';
import useClubRegistrationNewData from '../../../state/clubRegistrationNew/UseClubRegistrationNewData';
import useUsasClubStaffData from '../../../state/clubStaff/UseUsasClubStaffData';

import useForm from '@usas/ui-web/utils/UseForm';
import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';
import validate from './NewClubRegistrationStaffDetailValidation';
import Constants from '@usas/ui-web/utils/Constants';

const useClubRegistrationStaffDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [state, setState] = useState({ tryRedirect: false });

    const { clubRegistrationNavState } = useClubRegistrationNavData();
    const { clubRegistrationState } = useClubRegistrationNewData();
    const { usasClubStaffState, postUsasClubStaff, putUsasClubStaff } = useUsasClubStaffData();

    const { formState, errorState, updateFormState, handleSubmit, isSubmitting,
        isDirty, setIsDirty, setErrors, setFormData, onValueTextPairChanged } = useForm(getFormInitialState(), submitFormCallback, validate);

    const {
        environmentVariableState,
        HEAD_COACH_ORG_ROLE_ID,
        ASSISTANT_COACH_ORG_ROLE_ID,
        AGE_GROUP_COACH_ORG_ROLE_ID,
        SENIOR_COACH_ORG_ROLE_ID,
        SECOND_COACH_ORG_ROLE_ID,
        ORG_ROLE_FIELD_ID_HEAD_COACH_FULL_PART_TIME,
        ORG_ROLE_FIELD_ID_ASSISTANT_COACH_FULL_PART_TIME,
        ORG_ROLE_FIELD_ID_AGE_GROUP_COACH_FULL_PART_TIME,
        ORG_ROLE_FIELD_ID_SENIOR_COACH_FULL_PART_TIME,
        ORG_ROLE_FIELD_ID_SECOND_COACH_FULL_PART_TIME,
        CLUB_STAFF_ROLE_GROUP_ID,
        CLUB_STAFF_REGISTRATION_ROLE_GROUP_ID,
        CLUB_BOARD_MEMBER_ORG_ROLE_ID,
        JUNIOR_COACH_ORG_ROLE_ID,
        PROVISIONAL_COACH_ORG_ROLE_ID
    } = useEnvironmentVariableData();

    const coachRoleIds = [HEAD_COACH_ORG_ROLE_ID, ASSISTANT_COACH_ORG_ROLE_ID, AGE_GROUP_COACH_ORG_ROLE_ID, SENIOR_COACH_ORG_ROLE_ID, SECOND_COACH_ORG_ROLE_ID];

    const orgRoleToFieldIdMap = {
        [HEAD_COACH_ORG_ROLE_ID]: { id: ORG_ROLE_FIELD_ID_HEAD_COACH_FULL_PART_TIME },
        [ASSISTANT_COACH_ORG_ROLE_ID]: { id: ORG_ROLE_FIELD_ID_ASSISTANT_COACH_FULL_PART_TIME },
        [AGE_GROUP_COACH_ORG_ROLE_ID]: { id: ORG_ROLE_FIELD_ID_AGE_GROUP_COACH_FULL_PART_TIME },
        [SENIOR_COACH_ORG_ROLE_ID]: { id: ORG_ROLE_FIELD_ID_SENIOR_COACH_FULL_PART_TIME },
        [SECOND_COACH_ORG_ROLE_ID]: { id: ORG_ROLE_FIELD_ID_SECOND_COACH_FULL_PART_TIME }
    }

    const coachTypeCodeOptions = [
        { id: 'F', name: "Full Time" },
        { id: 'P', name: "Part Time" }];

    const onAddClicked = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        if (isDirty === false) {
            setIsDirty(true);
        }
        handleSubmit();
    }

    const onCancelClicked = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
        }

        const showGrid = true;
        navigate(NavLinks.CLUB_REGISTRATION_STAFF, { state: { showGrid } });
    }

    function submitFormCallback(formState) {
        if (formState.postPersonId) {
            let staffArray = usasClubStaffState.arrayData;
            let boardStaffArray = [];

            staffArray.forEach((staff) => {
                let boardStaffObj = {};
                let memberId = staff.memberId;
                staff.personOrgRole.forEach((role) => {
                    if (role.orgRoleId === CLUB_BOARD_MEMBER_ORG_ROLE_ID) {
                        boardStaffObj = {
                            memberId: memberId,
                            lastName: staff.lastName,
                            firstName: staff.firstName
                        };
                        boardStaffArray.push(boardStaffObj);
                    }
                });
            });

            const dupStaff = boardStaffArray.find(x => x.memberId === formState.memberId);
            if (dupStaff && formState.selectedOrgRoleId === CLUB_BOARD_MEMBER_ORG_ROLE_ID) {
                setErrors({
                    ...errorState,
                    apiError: `${dupStaff.firstName} ${dupStaff.lastName} (${dupStaff.memberId}) is already associated with this club's board members.`
                });
            } else {
                postUsasClubStaff(clubRegistrationState.objData.orgUnitId, CLUB_STAFF_ROLE_GROUP_ID, createPostStaffObj(formState), false);
                setState({ ...state, tryRedirect: true });
            }
        }
    }

    function createPostStaffObj(formState) {
        const personOrgRoleArray = [];
        const personOrgRoleFieldsArray = [];
        let personOrgRoleFieldObj = {};

        if (coachRoleIds.includes(location.state?.orgRoleId)) {
            personOrgRoleFieldObj = {
                fieldName: 'Full_Part_Time',
                orgRoleFieldId: orgRoleToFieldIdMap[location.state?.orgRoleId].id,
                fieldValue: formState.coachTypeCode
            };
        }

        if (Object.keys(personOrgRoleFieldObj).length > 0) {
            personOrgRoleFieldsArray.push(personOrgRoleFieldObj);
        }

        const personOrgRoleObj = {
            orgRoleId: location.state?.orgRoleId,
            orgRoleName: location.state?.orgRoleName,
            personOrgRoleFields: personOrgRoleFieldsArray
        };

        personOrgRoleArray.push(personOrgRoleObj);

        return {
            personId: formState.postPersonId,
            personOrgRole: personOrgRoleArray
        };
    }

    useEffect(() => {
        //Wait for add to complete before redirecting
        if (usasClubStaffState.isSaved === true && state.tryRedirect === true) {
            const showGrid = true;
            const addedStaff = true;
            navigate(NavLinks.CLUB_REGISTRATION_STAFF, { state: { showGrid, addedStaff } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usasClubStaffState, state.tryRedirect]);

    useEffect(() => {
        if (coachRoleIds.length > 0 && environmentVariableState.isLoaded === true) {

            let secondCoachMemberId = "";
            usasClubStaffState.arrayData.forEach((staff) => {
                staff.personOrgRole.forEach((role) => {
                    if (role.orgRoleId === SECOND_COACH_ORG_ROLE_ID ) {
                        secondCoachMemberId = staff.memberId;
                    }});
                });

            let headCoachMemberId = "";
            usasClubStaffState.arrayData.forEach((staff) => {
                staff.personOrgRole.forEach((role) => {
                    if (role.orgRoleId === HEAD_COACH_ORG_ROLE_ID ) {
                        headCoachMemberId = staff.memberId;
                    }});
                });
        
            setFormData({
                ...formState,
                coachRoleIds: coachRoleIds,
                selectedOrgRoleId: location.state?.orgRoleId,
                clubStaffRoleGroupId: CLUB_STAFF_REGISTRATION_ROLE_GROUP_ID,
                headCoachMemberId: headCoachMemberId,
                secondCoachMemberId: secondCoachMemberId,
                headCoachOrgRoleId: HEAD_COACH_ORG_ROLE_ID,
                secondCoachOrgRoleId: SECOND_COACH_ORG_ROLE_ID,
                juniorCoachOrgRoleId: JUNIOR_COACH_ORG_ROLE_ID,
                provisionalCoachOrgRoleId: PROVISIONAL_COACH_ORG_ROLE_ID
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function getFormInitialState() {
        return {
            personId: '',
            memberId: '',
            personOrgRole: [],
            postPersonId: '',
            coachTypeCode: '',
            coachTypeCodeName: '',
            coachRoleIds: [],
            selectedOrgRoleId: Constants.DEFAULT_ID,
            clubStaffRoleGroupId: Constants.DEFAULT_ID,
            selectedBoardMembers: [],
            headCoachMemberId: '',
            secondCoachMemberId: '',
            headCoachOrgRoleId: Constants.DEFAULT_ID,
            secondCoachOrgRoleId: Constants.DEFAULT_ID,
            juniorCoachOrgRoleId: Constants.DEFAULT_ID,
            provisionalCoachOrgRoleId: Constants.DEFAULT_ID
        }
    }

    return {
        clubRegistrationNavState,
        formState,
        errorState,
        onFormValueChanged: updateFormState,
        onValueTextPairChanged,
        onAddClicked,
        onCancelClicked,
        setFormData,
        handleSubmit,
        coachTypeCodeOptions,
        HEAD_COACH_ORG_ROLE_ID,
        ASSISTANT_COACH_ORG_ROLE_ID,
        AGE_GROUP_COACH_ORG_ROLE_ID,
        SENIOR_COACH_ORG_ROLE_ID,
        SECOND_COACH_ORG_ROLE_ID,
        orgRoleId: location.state?.orgRoleId,
        orgRoleName: location.state?.orgRoleName
    }
}

export default useClubRegistrationStaffDetail;